import type { ReactNode } from "react";
import { COLOR_TEXT_DIM, FONT_SIZE_BODY } from "../tokens.ts";
import { MenuButtonRow } from "./MenuPrimitives.tsx";

/**
 * Renders a label with the matching shortcut-key character underlined.
 * Returns the plain label string when no key is provided or no match found.
 */
export function renderLabelWithShortcut(
  label: string,
  key: string | undefined,
): ReactNode {
  if (!key) return label;
  const idx = label.toLowerCase().indexOf(key.toLowerCase());
  if (idx === -1) return label;
  return (
    <>
      {label.slice(0, idx)}
      <span style={{ textDecoration: "underline", textUnderlineOffset: "2px" }}>
        {label[idx]}
      </span>
      {label.slice(idx + 1)}
    </>
  );
}

/**
 * Standard menu item used in both dropdown menus and context menus.
 *
 * - `icon` — optional leading icon, rendered in a fixed 16px column so
 *   labels align when mixing icon and no-icon items in the same menu.
 * - `shortcut` — right-aligned display string (e.g. "⌘S").
 * - `shortcutKey` — single lowercase letter wired to Popover keyboard
 *   navigation; the matching character in `label` is underlined.
 * - `danger` — renders the label in danger color (destructive actions).
 * - `popoverItem` — registers the item for Popover arrow-key navigation
 *   and active highlighting (default `true`).
 */
export function MenuItem({
  label,
  icon,
  shortcut,
  shortcutKey,
  danger,
  active,
  disabled,
  popoverItem = true,
  onClick,
}: {
  label: string;
  icon?: ReactNode;
  shortcut?: string;
  shortcutKey?: string;
  danger?: boolean;
  active?: boolean;
  disabled?: boolean;
  popoverItem?: boolean;
  onClick: () => void;
}) {
  return (
    <MenuButtonRow
      data-popover-item={!disabled && popoverItem ? true : undefined}
      data-popover-shortcut={shortcutKey && !disabled ? shortcutKey : undefined}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      active={active}
      danger={danger}
      style={{ whiteSpace: "nowrap" }}
    >
      {icon !== undefined && (
        <span
          style={{
            width: 16,
            display: "inline-flex",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </span>
      )}
      <span style={{ flex: 1 }}>
        {shortcutKey ? renderLabelWithShortcut(label, shortcutKey) : label}
      </span>
      {shortcut && (
        <span
          style={{
            fontSize: FONT_SIZE_BODY - 1,
            color: COLOR_TEXT_DIM,
            marginLeft: 16,
            flexShrink: 0,
          }}
        >
          {shortcut}
        </span>
      )}
    </MenuButtonRow>
  );
}
